// Port of Offline/OfflineStore.swift (TrailBivvyKit): persists offline regions, their download plans and the list of
// data files they pinned, in IndexedDB (`kvStore('offlineRegions')`, one record per region keyed by its id).
//
// Swift wrote regions.json + per-region dataset.json/plan.json; the PWA keeps one record per region:
// `{ region, plan (compact StoredPlan), dataURLs }`. The route data itself lives in Cache Storage
// (`PinnedDataCache`). Every write goes through one serial queue, so concurrent progress checkpoints of the same
// region never interleave their read-modify-write cycles. Records are cloned on the way in and out (the in-memory
// test store would otherwise share objects with callers).

import type { OfflineRegion } from '../core/models';
import { kvStore, type KeyValueStore } from '../storage/idb';
import { planFromStored, storedPlanFrom, type OfflinePackagePlan, type StoredPlan } from './OfflinePackagePlanner';

/** One persisted offline region. */
export interface StoredOfflineRegion {
  region: OfflineRegion;
  /** The tile plan, so an interrupted download can resume after a relaunch and deletion knows its tiles. */
  plan?: StoredPlan;
  /** Absolute URLs of the data files pinned for this region (trail pack JSON, world rules layer). */
  dataURLs: string[];
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

export class OfflineStore {
  private queue: Promise<unknown> = Promise.resolve();

  constructor(private readonly kv: KeyValueStore<StoredOfflineRegion> = kvStore<StoredOfflineRegion>('offlineRegions')) {}

  /** Runs `work` after every earlier write (errors do not block later writes). */
  private serial<T>(work: () => Promise<T>): Promise<T> {
    const run = this.queue.then(work, work);
    this.queue = run.catch(() => undefined);
    return run;
  }

  /** All regions, oldest first (then by id). Unreadable records are skipped. */
  async regions(): Promise<OfflineRegion[]> {
    const records = await this.records();
    return records.map((r) => r.region).sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
  }

  /** Every stored record, regions oldest first. */
  async records(): Promise<StoredOfflineRegion[]> {
    const values = await this.kv.values();
    return values
      .filter((v): v is StoredOfflineRegion => typeof v === 'object' && v !== null && typeof v.region?.id === 'string')
      .map(clone)
      .sort((a, b) => a.region.createdAt - b.region.createdAt || a.region.id.localeCompare(b.region.id));
  }

  async record(id: string): Promise<StoredOfflineRegion | undefined> {
    const value = await this.kv.get(id);
    return value ? clone(value) : undefined;
  }

  /** The region with `id`, if any. */
  async region(id: string): Promise<OfflineRegion | undefined> {
    return (await this.record(id))?.region;
  }

  /** Inserts or replaces a region, keeping its plan and data files. */
  save(region: OfflineRegion): Promise<void> {
    return this.serial(async () => {
      const existing = await this.kv.get(region.id);
      await this.kv.set(region.id, clone({ region, plan: existing?.plan, dataURLs: existing?.dataURLs ?? [] }));
    });
  }

  /** Writes a whole record (region + plan + data files). */
  saveRecord(record: StoredOfflineRegion): Promise<void> {
    return this.serial(() => this.kv.set(record.region.id, clone(record)));
  }

  /** Persists a download plan (and the data files) for an existing region. */
  savePlan(plan: OfflinePackagePlan, dataURLs: string[]): Promise<void> {
    return this.serial(async () => {
      const existing = await this.kv.get(plan.region.id);
      const region = existing?.region ?? plan.region;
      await this.kv.set(plan.region.id, clone({ region, plan: storedPlanFrom(plan), dataURLs }));
    });
  }

  /** The persisted plan of a region, combined with the current region record. */
  async plan(regionID: string): Promise<OfflinePackagePlan | undefined> {
    const record = await this.record(regionID);
    if (!record?.plan) return undefined;
    return planFromStored(record.plan, record.region);
  }

  /** Records of every region except `regionID`. */
  async recordsExcluding(regionID: string): Promise<StoredOfflineRegion[]> {
    return (await this.records()).filter((r) => r.region.id !== regionID);
  }

  /** Removes a region and its plan. Unknown ids are ignored. */
  delete(regionID: string): Promise<void> {
    return this.serial(() => this.kv.delete(regionID));
  }

  /**
   * Applies `change` to the stored region (if it still exists) and persists it, leaving the plan and data files
   * untouched. Returns the updated region, or undefined when it no longer exists.
   */
  updateRegion(id: string, change: (region: OfflineRegion) => void): Promise<OfflineRegion | undefined> {
    return this.serial(async () => {
      const existing = await this.kv.get(id);
      if (!existing) return undefined;
      const record = clone(existing);
      change(record.region);
      await this.kv.set(id, record);
      return clone(record.region);
    });
  }
}
